import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './i18n'; // Initialize i18n
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ErrorBoundary from './components/ErrorBoundary';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';

// Critical pages - load immediately (public-facing pages users might land on)
import Landing from './pages/Landing';
import PublicStatusUpdate from './pages/PublicStatusUpdate';
import PublicTrackingView from './pages/PublicTrackingView';

// Lazy-loaded pages - load on demand
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const TemplateBuilder = React.lazy(() => import('./pages/TemplateBuilder'));
const TemplateDetails = React.lazy(() => import('./pages/TemplateDetails'));
const TemplatesList = React.lazy(() => import('./pages/TemplatesList'));
const Settings = React.lazy(() => import('./pages/Settings'));
const Help = React.lazy(() => import('./pages/Help'));
const InstanceCreate = React.lazy(() => import('./pages/InstanceCreate'));
const InstancesList = React.lazy(() => import('./pages/InstancesList'));
const InstanceDetails = React.lazy(() => import('./pages/InstanceDetails'));
const Pricing = React.lazy(() => import('./pages/Pricing'));

// Auth pages
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail'));
const ResendVerification = React.lazy(() => import('./pages/ResendVerification'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));

// Legal pages
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const Terms = React.lazy(() => import('./pages/Terms'));

// Payment pages
const PaymentSuccess = React.lazy(() => import('./pages/PaymentSuccess'));
const PaymentFailure = React.lazy(() => import('./pages/PaymentFailure'));

// Blog pages
const UseCaseDetails = React.lazy(() => import('./pages/UseCaseDetails'));
const BlogList = React.lazy(() => import('./pages/BlogList'));
const BlogPost = React.lazy(() => import('./pages/BlogPost'));

// Admin pages
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = React.lazy(() => import('./pages/admin/AdminUsers'));
const AdminOrders = React.lazy(() => import('./pages/admin/AdminOrders'));
const AdminSettings = React.lazy(() => import('./pages/admin/AdminSettings'));
const AdminTemplates = React.lazy(() => import('./pages/admin/AdminTemplates'));
const AdminStatuses = React.lazy(() => import('./pages/admin/AdminStatuses'));
const AdminDiscountCodes = React.lazy(() => import('./pages/admin/AdminDiscountCodes'));
const AdminTestModeSettings = React.lazy(() => import('./pages/admin/AdminTestModeSettings'));

import RedirectHandler from './components/RedirectHandler';
import ScrollToTop from './components/ScrollToTop';

// Loading component for Suspense fallback
const LoadingFallback = () => (
    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontSize: '1.2rem',
        color: '#666'
    }}>
        Ładowanie...
    </div>
);

function App() {
    return (
        <ThemeProvider>
            <ErrorBoundary>
                <AuthProvider>
                    <BrowserRouter>
                        <ScrollToTop />
                        <RedirectHandler />
                        <Suspense fallback={<LoadingFallback />}>
                            <Routes>
                                {/* Public Routes */}
                                <Route path="/update/:token" element={<PublicStatusUpdate />} />
                                <Route path="/track/:token" element={<PublicTrackingView />} />
                                <Route path="/verify-email/:token" element={<VerifyEmail />} />
                                <Route path="/resend-verification" element={<ResendVerification />} />
                                <Route path="/forgot-password" element={<ForgotPassword />} />
                                <Route path="/reset-password/:token" element={<ResetPassword />} />
                                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                                <Route path="/terms" element={<Terms />} />

                                {/* Payment Routes */}
                                <Route
                                    path="/payment/success"
                                    element={
                                        <PrivateRoute>
                                            <PaymentSuccess />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/payment/failure"
                                    element={
                                        <PrivateRoute>
                                            <PaymentFailure />
                                        </PrivateRoute>
                                    }
                                />

                                {/* Protected Routes */}
                                <Route
                                    path="/dashboard"
                                    element={
                                        <PrivateRoute>
                                            <Dashboard />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/templates"
                                    element={
                                        <PrivateRoute>
                                            <TemplatesList />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/templates/new"
                                    element={
                                        <PrivateRoute>
                                            <TemplateBuilder />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/templates/:id/edit"
                                    element={
                                        <PrivateRoute>
                                            <TemplateBuilder />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/templates/:id"
                                    element={
                                        <PrivateRoute>
                                            <TemplateDetails />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/instances/new"
                                    element={
                                        <PrivateRoute>
                                            <InstanceCreate />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/instances/:id"
                                    element={
                                        <PrivateRoute>
                                            <InstanceDetails />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/instances"
                                    element={
                                        <PrivateRoute>
                                            <InstancesList />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/settings"
                                    element={
                                        <PrivateRoute>
                                            <Settings />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/help"
                                    element={
                                        <PrivateRoute>
                                            <Help />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/pricing"
                                    element={
                                        <PrivateRoute>
                                            <Pricing />
                                        </PrivateRoute>
                                    }
                                />

                                {/* Public Use Cases */}
                                <Route path="/zastosowania/:slug" element={<UseCaseDetails />} />

                                {/* Blog Routes */}
                                <Route path="/blog" element={<BlogList />} />
                                <Route path="/blog/:slug" element={<BlogPost />} />



                                {/* Admin Routes */}
                                <Route
                                    path="/admin"
                                    element={
                                        <AdminRoute>
                                            <AdminDashboard />
                                        </AdminRoute>
                                    }
                                />
                                <Route
                                    path="/admin/users"
                                    element={
                                        <AdminRoute>
                                            <AdminUsers />
                                        </AdminRoute>
                                    }
                                />
                                <Route
                                    path="/admin/orders"
                                    element={
                                        <AdminRoute>
                                            <AdminOrders />
                                        </AdminRoute>
                                    }
                                />
                                <Route
                                    path="/admin/settings"
                                    element={
                                        <AdminRoute>
                                            <AdminSettings />
                                        </AdminRoute>
                                    }
                                />
                                <Route
                                    path="/admin/templates"
                                    element={
                                        <AdminRoute>
                                            <AdminTemplates />
                                        </AdminRoute>
                                    }
                                />
                                <Route
                                    path="/admin/statuses"
                                    element={
                                        <AdminRoute>
                                            <AdminStatuses />
                                        </AdminRoute>
                                    }
                                />
                                <Route
                                    path="/admin/discount-codes"
                                    element={
                                        <AdminRoute>
                                            <AdminDiscountCodes />
                                        </AdminRoute>
                                    }
                                />
                                <Route
                                    path="/admin/test-mode"
                                    element={
                                        <AdminRoute>
                                            <AdminTestModeSettings />
                                        </AdminRoute>
                                    }
                                />

                                {/* Default Route */}
                                <Route path="/" element={<Landing />} />
                                <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                        </Suspense>
                    </BrowserRouter>
                </AuthProvider>
            </ErrorBoundary>
        </ThemeProvider>
    );
}

export default App;
